import React, { useState } from 'react';
import { LifeBuoy, X, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/config';

// REUSABLE SUPPORT TICKET FORM - WITH FINAL CORRECTED SUBMISSION VALUES
export const SupportTicketForm: React.FC<{ onSubmitSuccess: () => void }> = ({ onSubmitSuccess }) => {
    const { token } = useAuth();
    const [formData, setFormData] = useState({
        category: '',
        priority: '',
        subject: '',
        description: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) {
            setError("You must be logged in to submit a ticket.");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/support`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to submit ticket.');
            }

            setIsSubmitted(true);
            setTimeout(() => {
                onSubmitSuccess();
                setIsSubmitted(false);
                setFormData({ category: '', priority: '', subject: '', description: '' });
            }, 2000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto">
                    <Send className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold text-white">Ticket Created!</h4>
                <p className="text-sm text-kala-400">
                    Your ticket has been successfully submitted.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
                <div className="bg-red-500/20 p-3 rounded text-xs text-red-400 flex gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <p>{error}</p>
                </div>
            )}
            <div>
                <label className="block text-xs text-kala-400 font-bold uppercase mb-1">Issue Category</label>
                <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-kala-800 border border-kala-700 rounded-lg px-3 py-2 text-sm text-white focus:border-kala-secondary outline-none"
                >
                    <option value="" disabled>Please select a category...</option>
                    <option value="General">General Inquiry</option>
                    <option value="Technical">Platform Bug / Glitch</option>
                    <option value="Account">Account Verification</option>
                    <option value="Payment">Payment Gateway Failure</option>
                    <option value="Proposal">Proposal Dispute</option>
                    <option value="Transaction">Transaction / Escrow Issue</option>
                    <option value="Appeal">Zero Tolerance Appeal</option>
                </select>
            </div>
            <div>
                <label className="block text-xs text-kala-400 font-bold uppercase mb-1">Priority</label>
                <select
                    required
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full bg-kala-800 border border-kala-700 rounded-lg px-3 py-2 text-sm text-white focus:border-kala-secondary outline-none"
                >
                    <option value="" disabled>Please set a priority...</option>
                    <option value="Low">Low - General Question</option>
                    <option value="Medium">Medium - Feature Issue, Non-blocking</option>
                    <option value="High">High - Time-Sensitive (e.g., Stuck Transaction)</option>
                    {/* The value must be "Critical" to match the backend's mapping */}
                    <option value="Critical">Urgent - Account Access Loss, Appeal</option>
                </select>
            </div>
            <div>
                <label className="block text-xs text-kala-400 font-bold uppercase mb-1">Subject</label>
                <input required type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Brief summary..." className="w-full bg-kala-800 border border-kala-700 rounded-lg px-3 py-2 text-sm text-white focus:border-kala-secondary outline-none" />
            </div>
            <div>
                <label className="block text-xs text-kala-400 font-bold uppercase mb-1">Description</label>
                <textarea required rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Describe the issue in detail..." className="w-full bg-kala-800 border border-kala-700 rounded-lg px-3 py-2 text-sm text-white focus:border-kala-secondary outline-none resize-none"></textarea>
            </div>
            <div className="bg-kala-800/50 p-3 rounded text-xs text-kala-400 flex gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <p>For transaction disputes, please include the TX Hash if available.</p>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold py-3 rounded-xl transition-colors disabled:opacity-50">
                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
        </form>
    );
};


const SupportWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-kala-secondary text-kala-900 p-4 rounded-full shadow-lg shadow-cyan-900/20 hover:scale-110 transition-transform font-bold flex items-center gap-2"
      >
        {isOpen ? <X className="w-6 h-6" /> : <LifeBuoy className="w-6 h-6" />}
        {!isOpen && (
          <span className="hidden md:inline pr-1">Help & Support</span>
        )}
      </button>

      {isOpen && (
        <div className="mt-2 w-80 md:w-96 bg-kala-900 border border-kala-700 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-10 fade-in origin-bottom-right">
          <div className="bg-kala-800 p-4 border-b border-kala-700 flex justify-between items-center">
            <h3 className="font-bold text-white flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-kala-secondary" /> Support Center
            </h3>
            <span className="text-[10px] text-kala-500 uppercase font-bold tracking-wider">Tiered Support</span>
          </div>
          
          <SupportTicketForm onSubmitSuccess={() => setIsOpen(false)} />

          <div className="p-3 bg-kala-900 border-t border-kala-700 text-center">
            <p className="text-[10px] text-kala-500">
              Response times vary by{' '}
              <span className="text-white cursor-pointer hover:underline">Support Plan</span>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportWidget;
